import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SUPPORT_EMAIL } from '../../lib/authRedirects';

export function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="grain min-h-screen">
      <header className="app-header px-4 pb-4">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="btn-quiet p-2"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="display text-xl font-semibold sm:text-2xl">Privacy Policy</h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12">
        <div className="cine-card space-y-6 rounded-2xl p-8 sm:p-10">
          <div className="max-w-none">
            <h2 className="display mb-2 text-2xl font-semibold">Privacy Policy for MovieMe</h2>
            <p className="mb-6 text-sm text-muted-foreground/70">Last updated: August 27, 2026</p>

            <p className="mb-6 text-muted-foreground">
              MovieMe (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) respects your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard information when you use the MovieMe website at
              moviemeapp.com and the MovieMe iOS app (together, the &quot;App&quot;). By using the App, you agree
              to this policy.
            </p>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">1. Information We Collect</h3>
              <p className="mb-4 text-muted-foreground">
                When you use MovieMe, we may collect the following:
              </p>

              <h4 className="mb-2 text-lg font-medium">1.1 Account information</h4>
              <ul className="mb-4 list-disc space-y-2 pl-6 text-muted-foreground">
                <li>Email address (for signup, sign-in, and account emails)</li>
                <li>Name you provide at signup (if any)</li>
                <li>Authentication credentials managed by our auth provider (we do not store your
                  plaintext password on our servers)</li>
              </ul>

              <h4 className="mb-2 text-lg font-medium">1.2 App content and preferences</h4>
              <ul className="mb-4 list-disc space-y-2 pl-6 text-muted-foreground">
                <li>Answers you give in the recommendation flow (mood, genre, time, and similar
                  preferences)</li>
                <li>Watchlist and watched titles you save</li>
                <li>
                  Recommendation usage needed to enforce the free-tier limit (currently 30
                  recommendation pulls per UTC calendar month; initial results and each
                  &quot;load more&quot; batch use one pull)
                </li>
              </ul>

              <h4 className="mb-2 text-lg font-medium">1.3 Technical information</h4>
              <ul className="mb-4 list-disc space-y-2 pl-6 text-muted-foreground">
                <li>Device type and operating system (for example, iOS or web browser)</li>
                <li>Basic usage and diagnostic data needed to run and secure the App</li>
                <li>IP address (security, abuse prevention, and infrastructure logs)</li>
              </ul>

              <h4 className="mb-2 text-lg font-medium">1.4 Movie metadata</h4>
              <p className="text-muted-foreground">
                Movie titles, artwork, and related metadata are loaded from third-party movie data
                providers (such as TMDB) to show recommendations. That content is not your personal
                data, but your interaction with it (saves, watches) is linked to your account.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">2. How We Use Your Information</h3>
              <ul className="mb-4 list-disc space-y-2 pl-6 text-muted-foreground">
                <li>Provide recommendations and personalize suggestions</li>
                <li>Maintain your account, Watchlist, and Watched library</li>
                <li>Send account emails (confirmation, password reset)</li>
                <li>Enforce usage limits and prevent abuse</li>
                <li>Improve reliability, security, and product quality</li>
                <li>Comply with legal obligations when required</li>
              </ul>
              <p className="text-muted-foreground">
                We do not sell your personal information to advertisers.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">3. How We Share Information</h3>
              <p className="mb-4 text-muted-foreground">
                We do not sell or rent your personal information. We share data only as needed to
                operate the App:
              </p>
              <ul className="mb-4 list-disc space-y-2 pl-6 text-muted-foreground">
                <li>
                  When AI recommendations are enabled, MovieMe sends OpenAI the choices entered in
                  the recommendation flow (mood or genres, favorite movie titles, critical-acclaim
                  choice, and time period) plus movie titles marked Watched so OpenAI can generate
                  picks and avoid repeats. MovieMe does not send your email address or Watchlist to
                  OpenAI.
                </li>
                <li>
                  <span className="text-foreground">Service providers:</span> Supabase (authentication),
                  Neon (application database), OpenAI (generating recommendations), TMDB (movie
                  metadata and artwork), Vercel (hosting/API), and email delivery (for account
                  messages)
                </li>
                <li>Legal requests when required by law</li>
                <li>With your direction or consent</li>
              </ul>
              <p className="text-muted-foreground">
                We use service providers only to operate MovieMe and require them to handle personal
                data with protections consistent with this policy and applicable agreements.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">4. Storage and Security</h3>
              <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                <li>Account auth is handled by Supabase; app data is stored in Neon</li>
                <li>Traffic uses industry-standard encryption in transit (HTTPS / TLS)</li>
                <li>Access to production systems is limited to what is needed to run the product</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">5. Your Choices</h3>
              <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                <li>Update account details where the App allows</li>
                <li>
                  You can allow or withdraw permission for OpenAI-powered recommendations at any
                  time in Account → AI recommendations. Turning it off stops future recommendation
                  data from being sent to OpenAI and disables new AI recommendations until you
                  enable it again.
                </li>
                <li>
                  Delete your account and associated app data from Account → Delete Account in the
                  App, or by emailing {SUPPORT_EMAIL}
                </li>
                <li>Stop using the App at any time</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">6. Children</h3>
              <p className="text-muted-foreground">
                MovieMe is not directed to children under 13. We do not knowingly collect personal
                information from children under 13. If you believe we have, contact us and we will
                delete it.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">7. Third-Party Links</h3>
              <p className="text-muted-foreground">
                The App may link to external sites (for example, streaming or movie information
                pages). Their privacy practices are their own.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">8. Changes</h3>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will post the updated policy
                in the App and on our website with a new &quot;Last updated&quot; date.
              </p>
            </section>

            <section>
              <h3 className="display mb-4 text-xl font-semibold">9. Contact</h3>
              <ul className="mt-4 list-none space-y-2 text-muted-foreground">
                <li>Email: {SUPPORT_EMAIL}</li>
                <li>Website: https://www.moviemeapp.com</li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <footer className="mt-12 border-t border-white/[0.06] py-8">
        <div className="mx-auto max-w-7xl px-4">
          <p className="text-center text-sm text-muted-foreground/70">
            © 2026 MovieMe. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
